import { User } from "@prisma/client";
import React, { forwardRef } from "react";

import { UserAvatar } from "~/modules/users/UserAvatar";
import { UserAvatarWithName } from "~/modules/users/UserAvatarWithName";
import { parseUserDisplayName, UserFullNameText } from "~/modules/users/UserFullNameText";
import {
  CloseButton,
  Flex,
  MultiSelect,
  type MultiSelectProps,
  type MultiSelectValueProps,
  mantineColorRamps,
  Select,
  type SelectProps,
} from "~/ui";
import { api } from "~/utils/api";
import { findNeedleInHaystacks } from "~/utils/searching";

type UserLikeObjectType = Pick<User, "firstName" | "lastName" | "profileImageUrl" | "emailAddress">;

const useGetOrgUserOptions = () => {
  const { data } = api.users.searchUsersWithRoles.useQuery({});
  return (data || []).map(u => ({
    value: u.id,
    label: parseUserDisplayName(u) ?? undefined,
    firstName: u.firstName,
    lastName: u.lastName,
    emailAddress: u.emailAddress,
    profileImageUrl: u.profileImage,
  }));
};

type OrgUserSelectItem = ReturnType<typeof useGetOrgUserOptions>[number];

/**
 * Hook to fetch user data for display purposes.
 *
 * **IMPORTANT**: This hook uses `users.getForDisplay` which does NOT enforce
 * org membership. It will return users even if they've left the organization.
 *
 * **Use Only For**: Display/audit trails ("Created By", "Assigned To", etc.)
 * **Do NOT Use For**: Operations, permissions checks, or authorization
 *
 * For operations requiring active org membership, fetch the user separately
 * using `api.users.get.useQuery()` which enforces RBACUserRole filtering.
 *
 * @example
 * // ✅ GOOD - Display only
 * <OrgUserDisplay idOrUser={transaction.createdById} />
 *
 * @example
 * // ❌ BAD - Operations (use api.users.get instead)
 * const user = useGetOrgUser(userId);
 * await assignTask(user.id); // User might not be in org!
 */
const useGetOrgUser = (idOrUser: string | null | undefined | UserLikeObjectType) => {
  const { id, enabled } = typeof idOrUser === "string" ? { id: idOrUser, enabled: true } : { id: "", enabled: false };
  // Use getForDisplay for historical user data (e.g., "Created By" on transactions)
  // This doesn't enforce org membership, allowing us to show users who may have left the org
  const getUserQuery = api.users.getForDisplay.useQuery(
    { id },
    {
      enabled,
      retry: false, // Don't retry on 404
    },
  );

  return (enabled ? getUserQuery.data : idOrUser) as UserLikeObjectType | undefined;
};

interface OrgUserItemProps extends React.ComponentPropsWithoutRef<"div"> {
  firstName: string | null;
  lastName: string | null;
  emailAddress: string | null;
  profileImageUrl: string | null;
}
const OrgUserItem = forwardRef<HTMLDivElement, OrgUserItemProps>(
  ({ firstName, lastName, emailAddress, profileImageUrl, ...props }: OrgUserItemProps, ref) => {
    const user = {
      firstName: firstName,
      lastName: lastName,
      emailAddress: emailAddress,
      profileImageUrl: profileImageUrl,
    };
    return (
      <Flex ref={ref} {...props} direction="row" style={{ height: 32 }}>
        <UserAvatarWithName user={user} />
      </Flex>
    );
  },
);
OrgUserItem.displayName = "OrgUserItem";

interface OrgUserValueProps extends MultiSelectValueProps<OrgUserSelectItem> {
  firstName: string | null;
  lastName: string | null;
  emailAddress: string | null;
  profileImageUrl: string | null;
}
const OrgUserValue = ({ firstName, lastName, emailAddress, profileImageUrl, onRemove, ...props }: OrgUserValueProps) => {
  const user = {
    firstName: firstName,
    lastName: lastName,
    emailAddress: emailAddress,
    profileImageUrl: profileImageUrl,
  };
  return (
    <Flex
      {...props}
      direction="row"
      h={28}
      p="xs"
      align="center"
      style={{ border: `1px solid ${mantineColorRamps.gray[1]}`, borderRadius: 4 }}
    >
      <UserAvatar user={user} mr="xs" />
      <CloseButton onMouseDown={onRemove} variant="transparent" size={16} iconSize={14} />
    </Flex>
  );
};

type OrgUserSelectProps = Omit<SelectProps<OrgUserSelectItem>, "data" | "itemComponent">;
export const OrgUserSelect = (props: OrgUserSelectProps) => {
  const data = useGetOrgUserOptions();
  return (
    <Select<OrgUserSelectItem>
      placeholder="Users"
      searchable
      clearable
      {...props}
      itemComponent={OrgUserItem}
      data={data}
      filter={(value, item) =>
        findNeedleInHaystacks(value, item.firstName ?? undefined, item.lastName ?? undefined, item.emailAddress ?? undefined) != undefined
      }
    />
  );
};

type OrgUserMultiSelectProps = Omit<MultiSelectProps<OrgUserSelectItem>, "data" | "itemComponent" | "valueComponent">;
export const OrgUserMultiSelect = (props: OrgUserMultiSelectProps) => {
  const data = useGetOrgUserOptions();
  return (
    <MultiSelect<OrgUserSelectItem>
      placeholder="Users"
      searchable
      clearable
      {...props}
      itemComponent={OrgUserItem}
      valueComponent={OrgUserValue}
      data={data}
      filter={(value, selected, item) =>
        !selected &&
        findNeedleInHaystacks(value, item.firstName ?? undefined, item.lastName ?? undefined, item.emailAddress ?? undefined) != undefined
      }
    />
  );
};

type OrgUserDisplayProps = {
  idOrUser: string | null | undefined | UserLikeObjectType;
  hideAvatar?: boolean;
  span?: boolean;
};
export const OrgUserDisplay = ({ idOrUser, hideAvatar, span }: OrgUserDisplayProps) => {
  // Check if this is the sentinel user ID (to avoid API call)
  const isSentinelUser = typeof idOrUser === "string" && idOrUser === "00000000-0000-0000-0000-000000000000";

  // Fetch user data (disabled for sentinel user)
  const user = useGetOrgUser(isSentinelUser ? undefined : idOrUser);

  // Handle null/undefined input
  if (idOrUser == undefined) {
    return <></>;
  }

  // Handle sentinel "System User" ID specifically
  if (isSentinelUser) {
    return <>System User</>;
  }

  // Handle when user fetch fails or returns null (deleted users, errors, etc.)
  if (user == undefined) {
    return <>Unknown</>;
  }

  if (hideAvatar) {
    return <UserFullNameText span={span} h="auto" user={user} />;
  }
  return <UserAvatarWithName textProps={{ span }} user={user as User} />;
};
